import { ChangeEvent, FC, Ref } from 'react';
import classNames from 'classnames';

import {
  addressAsString,
  Address as AddressInput,
  Button,
  Icon,
  ILocalAddress,
  Input,
  Spinner,
  useData,
} from '@/components';
import { getDeliveryScheduleByAddress } from '@/utils';

import { useStoreLocatorContext } from './context';
import { CURRENT_LOCATION_LABEL } from './constants';

import styles from './storelocator.module.scss';

interface IDeliverySectionProps {
  handleUseCurrentLocation: () => void;
  loadingLocation: boolean;
  showInstructionText: boolean;
  ref?: Ref<HTMLInputElement | HTMLTextAreaElement> | null;
}

const ADDRESS_INPUT_ID = 'addres_input';

const DeliverySection: FC<IDeliverySectionProps> = ({
  showInstructionText,
  loadingLocation,
  handleUseCurrentLocation,
  ref: inputRef,
}) => {
  const {
    isDeliveryAddressInvalid,
    setDeliveryAddress,
    deliveryAddress,
    singleStore,
    isPickup,
    setIsDeliveryAddressInvalid,
    setSelectedStoreId,
    deliverySectionTitle,
  } = useStoreLocatorContext();
  const { store } = useData();

  const onAddressChange = (address: ILocalAddress) => {
    if (!address) {
      setIsDeliveryAddressInvalid(false);
      return;
    }

    const ds = getDeliveryScheduleByAddress(address, store);

    if (!ds) {
      setIsDeliveryAddressInvalid(true);
      return;
    }

    setIsDeliveryAddressInvalid(false);
    setDeliveryAddress(address);
    setSelectedStoreId(ds?.stores_provider_id);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    setDeliveryAddress({
      ...deliveryAddress,
      address2: event?.target?.value ?? '',
    });
  };

  return (
    <div className={styles.delivery_section}>
      <Button
        ariaLabel={CURRENT_LOCATION_LABEL}
        className={classNames(styles.current_location, {
          [styles.hidden]: singleStore && isPickup,
        })}
        onClick={handleUseCurrentLocation}
      >
        {loadingLocation ? (
          <Spinner className={styles.crosshairs_icon} sm />
        ) : (
          <Icon className={styles.crosshairs_icon} name="crosshairs" />
        )}
        <span>{CURRENT_LOCATION_LABEL}</span>
      </Button>
      {showInstructionText && (
        <p className={styles.delivery_section__title}>{deliverySectionTitle}</p>
      )}
      <div className={styles.delivery_section__form}>
        <AddressInput
          country={['us']}
          feedback={
            'Sorry, we don’t deliver there. Want to pick up your order instead?'
          }
          id={ADDRESS_INPUT_ID}
          isClearable={!!addressAsString(deliveryAddress)}
          isInvalid={isDeliveryAddressInvalid}
          isSmall
          label="Your address"
          name={ADDRESS_INPUT_ID}
          onAddressChange={onAddressChange}
          onAddressClear={() => {
            setIsDeliveryAddressInvalid(false);
            setSelectedStoreId(undefined as unknown as string);
          }}
          placeholder="Your address"
          ref={inputRef}
          value={deliveryAddress ? addressAsString(deliveryAddress) ?? '' : ''}
        />
        <Input
          inputLabel="Apartment or suite"
          isSmall
          name="address2"
          onChange={handleChange}
          placeholder="Apartment or suite (optional)"
          value={deliveryAddress ? deliveryAddress?.address2 ?? '' : ''}
        />
      </div>
    </div>
  );
};

export { DeliverySection };
